import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowLeft, Moon, Sun, Check, X, Activity, Monitor, BellRing, Volume2, ChevronRight, Globe, Lock, Shield } from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useLanguage } from '../../contexts/LanguageContext';
import withTranslation from '../../hoc/withTranslation';

const SettingsScreen = ({ t, currentLanguage }) => {
  const { 
    navigateToScreen, 
    isDarkMode, 
    toggleTheme, 
    fontSizeScale, 
    setGlobalFontSize,
    reducedMotion,
    toggleReducedMotion
  } = useUI();
  
  const { languages, changeLanguage } = useLanguage();
  
  // State for displaying feedback when settings are changed
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  
  // Touch event handling
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });

  // New ref for main content container
  const contentRef = useRef(null);

  // Handle changing font size with feedback
  const handleFontSizeChange = useCallback((value) => {
    setGlobalFontSize(value);
    showSettingsFeedback(t('settings.fontSizeChanged', 'Yazı boyutu değiştirildi'));
  }, [setGlobalFontSize, t]);
  
  // Handle theme toggle with feedback
  const handleThemeToggle = useCallback(() => {
    toggleTheme();
    showSettingsFeedback(isDarkMode 
      ? t('settings.lightModeEnabled', 'Aydınlık tema etkinleştirildi') 
      : t('settings.darkModeEnabled', 'Karanlık tema etkinleştirildi'));
  }, [toggleTheme, isDarkMode, t]);
  
  // Handle language change with feedback
  const handleLanguageChange = useCallback((code) => {
    changeLanguage(code);
    const langName = languages.find(l => l.code === code)?.name || '';
    showSettingsFeedback(t('settings.languageChanged', 'Dil değiştirildi: {lang}', { lang: langName }));
  }, [changeLanguage, languages, t]);
  
  // Display temporary feedback message
  const showSettingsFeedback = useCallback((message) => {
    setFeedbackMessage(message);
    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
    }, 2000);
  }, []);
  
  // Modified touch event handlers for better mobile interaction
  const handleTouchStart = useCallback((e) => {
    // Only track touch for buttons, not for scrolling
    if (e.currentTarget.tagName === 'BUTTON') {
      const touch = e.touches[0];
      setTouchStartTime(new Date().getTime());
      setTouchStartPos({ 
        x: touch.clientX, 
        y: touch.clientY 
      });
    }
  }, []);

  const handleTouchEnd = useCallback((e, action) => {
    // Only process for buttons, not for general scrolling
    if (e.currentTarget.tagName === 'BUTTON') {
      const touch = e.changedTouches[0];
      const touchEndTime = new Date().getTime();
      const touchDuration = touchEndTime - touchStartTime;
      
      // Only register as tap if it's quick and didn't move much
      const touchDistance = Math.sqrt(
        Math.pow(touch.clientX - touchStartPos.x, 2) + 
        Math.pow(touch.clientY - touchStartPos.y, 2)
      );
      
      if (touchDuration < 300 && touchDistance < 20 && typeof action === 'function') {
        action();
      }
    }
  }, [touchStartTime, touchStartPos]);
  
  // Get current language
  const getCurrentLanguageObj = useCallback(() => {
    return languages.find(l => l.code === currentLanguage) || languages[0];
  }, [languages, currentLanguage]);
  
  const currentLang = getCurrentLanguageObj();

  // Improve touch accessibility
  useEffect(() => {
    // Apply larger touch targets for better touch interaction
    const touchTargets = document.querySelectorAll('button, .touch-target');
    touchTargets.forEach(target => {
      if (target.style) {
        target.style.minHeight = '44px'; // Minimum recommended touch target size
      }
    });
  }, []);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 flex items-center shadow-sm sticky top-0 z-10`}>
        <button 
          onClick={() => navigateToScreen('profile')}
          className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          onTouchStart={handleTouchStart}
          onTouchEnd={(e) => handleTouchEnd(e, () => navigateToScreen('profile'))}
          style={{ minHeight: '44px', minWidth: '44px' }} // Ensure good touch target size
        >
          <ArrowLeft className={isDarkMode ? 'text-white' : 'text-gray-800'} size={24} />
        </button>
        <h1 className={`ml-4 text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('settings.appSettings', 'Uygulama Ayarları')}
        </h1>
      </div>

      {/* Scrollable Content Area */}
      <div 
        ref={contentRef} 
        className="p-4 space-y-6 overflow-y-auto pb-24" 
        style={{ 
          height: 'calc(100vh - 68px)', // Height minus header
          WebkitOverflowScrolling: 'touch', // Smooth scrolling for iOS
        }}
      >
        {/* Theme Settings */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center space-x-2 mb-4">
            <Monitor className={`${isDarkMode ? 'text-indigo-400' : 'text-blue-600'}`} size={20} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('settings.themeAndAppearance', 'Tema ve Görünüm')}
            </h2>
          </div>
          
          {/* Theme Selection */}
          <div className="mb-6">
            <h3 className={`font-medium mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {t('settings.colorTheme', 'Renk Teması')}
            </h3>
            <div className="flex space-x-4">
              <button 
                className={`flex-1 p-4 rounded-xl touch-target ${!isDarkMode ? 'bg-blue-100 border-2 border-blue-500' : 'bg-white/5 border border-gray-700'}`}
                onClick={() => { if (isDarkMode) handleThemeToggle(); }}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e, () => { if (isDarkMode) handleThemeToggle(); })}
                aria-label={t('settings.switchToLightMode', 'Aydınlık moda geç')}
                style={{ minHeight: '120px' }} // Larger touch area
              >
                <div className="h-16 bg-white rounded-lg mb-2 shadow-sm flex items-center justify-center">
                  <Sun className={!isDarkMode ? "text-yellow-500" : "text-gray-400"} size={24} />
                </div>
                <p className={`text-sm text-center ${isDarkMode ? 'text-gray-200' : 'text-gray-800 font-medium'}`}>
                  {t('settings.lightMode', 'Aydınlık')}
                </p>
              </button>
              <button 
                className={`flex-1 p-4 rounded-xl touch-target ${isDarkMode ? 'bg-indigo-900/30 border-2 border-indigo-500' : 'bg-gray-100 border border-gray-300'}`}
                onClick={() => { if (!isDarkMode) handleThemeToggle(); }}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e, () => { if (!isDarkMode) handleThemeToggle(); })}
                aria-label={t('settings.switchToDarkMode', 'Karanlık moda geç')}
                style={{ minHeight: '120px' }} // Larger touch area
              >
                <div className="h-16 bg-gray-800 rounded-lg mb-2 shadow-sm flex items-center justify-center">
                  <Moon className={isDarkMode ? "text-indigo-300" : "text-gray-500"} size={24} />
                </div>
                <p className={`text-sm text-center ${isDarkMode ? 'text-white font-medium' : 'text-gray-700'}`}>
                  {t('settings.darkMode', 'Karanlık')}
                </p>
              </button>
            </div>
          </div>
          
          {/* Font Size */}
          <div className="mb-3">
            <h3 className={`font-medium mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {t('settings.fontSize', 'Yazı Boyutu')}
            </h3>
            <div className="flex space-x-2">
              {[0.8, 1.0, 1.15, 1.3].map(scale => (
                <button
                  key={scale}
                  onClick={() => handleFontSizeChange(scale)}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={(e) => handleTouchEnd(e, () => handleFontSizeChange(scale))}
                  className={`flex-1 py-3 rounded-lg touch-target ${fontSizeScale === scale 
                    ? (isDarkMode ? 'bg-indigo-600 text-white' : 'bg-blue-600 text-white') 
                    : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800')}`}
                  style={{ minHeight: '50px' }} // Ensure good touch target size
                  aria-label={`${t('settings.setFontSize', 'Yazı boyutunu ayarla')}: ${
                    scale === 0.8 ? t('settings.fontSizeSmall', 'Küçük') : 
                    scale === 1.0 ? t('settings.fontSizeMedium', 'Normal') : 
                    scale === 1.15 ? t('settings.fontSizeLarge', 'Büyük') : 
                    t('settings.fontSizeExtraLarge', 'Çok Büyük')
                  }`}
                >
                  {scale === 0.8 ? t('settings.fontSizeSmall', 'Küçük') : 
                   scale === 1.0 ? t('settings.fontSizeMedium', 'Normal') : 
                   scale === 1.15 ? t('settings.fontSizeLarge', 'Büyük') : 
                   t('settings.fontSizeExtraLarge', 'Çok Büyük')}
                </button>
              ))}
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('settings.fontSizeDescription', 'Yazı boyutunu daha iyi okunabilirlik için artırın')}
            </p>
          </div>
          
          {/* Motion & Effects Settings */}
          <div className="mt-5 pt-5 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}">
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {t('settings.motionAndEffects', 'Hareket ve Efektler')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between touch-target" 
                style={{ minHeight: '50px' }} // Ensure good touch target size
              >
                <div className="flex items-center space-x-3">
                  <Activity size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                  <span className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {t('settings.reduceMotion', 'Hareketi Azalt')}
                  </span>
                </div>
                <button 
                  className={`relative inline-flex h-6 w-11 items-center rounded-full touch-target
                    ${reducedMotion
                      ? isDarkMode ? 'bg-indigo-600' : 'bg-blue-600'
                      : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                  onClick={toggleReducedMotion}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={(e) => handleTouchEnd(e, toggleReducedMotion)}
                  aria-pressed={reducedMotion}
                  role="switch"
                  aria-label={t('settings.reduceMotion', 'Hareketi Azalt')}
                  style={{ minHeight: '44px', minWidth: '44px' }} // Ensure good touch target size
                >
                  <span 
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300
                      ${reducedMotion ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Language Settings */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center space-x-2 mb-4">
            <Globe className={`${isDarkMode ? 'text-indigo-400' : 'text-blue-600'}`} size={20} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('settings.languageAndRegion', 'Dil ve Bölge')}
            </h2>
          </div>
          
          <div className="space-y-3 mb-2">
            {languages.map(lang => {
              const isActive = currentLanguage === lang.code;
              const buttonClass = isActive
                ? isDarkMode 
                  ? 'border-indigo-500 bg-indigo-900/20 text-indigo-300' 
                  : 'border-blue-500 bg-blue-50 text-blue-700'
                : isDarkMode 
                  ? 'border-gray-700 hover:bg-gray-700 text-gray-200' 
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700';
                
              return (
                <button
                  key={lang.code}
                  className={`flex items-center justify-between p-4 rounded-lg border w-full touch-target ${buttonClass} transition-colors`}
                  onClick={() => handleLanguageChange(lang.code)}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={(e) => handleTouchEnd(e, () => handleLanguageChange(lang.code))}
                  aria-pressed={isActive}
                  style={{ minHeight: '60px' }} // Ensure good touch target size
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                  </div>
                  
                  {isActive && (
                    <Check className={`h-5 w-5 ${isDarkMode ? 'text-indigo-300' : 'text-blue-500'}`} />
                  )}
                </button>
              );
            })}
          </div>
          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('settings.languageChangeNote', 'Dil değişiklikleri anında uygulanır ve kaydedilir.')}
          </p>
        </div>
        
        {/* Notification Settings (Preview) */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center space-x-2 mb-4">
            <BellRing className={`${isDarkMode ? 'text-indigo-400' : 'text-blue-600'}`} size={20} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('settings.notifications', 'Bildirimler')}
            </h2>
          </div>
          
          <div className={`p-4 rounded-lg border border-dashed flex items-center justify-between touch-target
            ${isDarkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-300 bg-gray-50'}`}
            style={{ minHeight: '60px' }} // Ensure good touch target size
          >
            <div className="flex items-center space-x-3">
              <Volume2 size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
              <div>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {t('settings.notificationSettings', 'Bildirim Ayarları')}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('settings.comingSoon', 'Yakında Gelecek')}
                </p>
              </div>
            </div>
            <ChevronRight size={16} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
          </div>
        </div>

        {/* Security & Password Settings */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center space-x-2 mb-4">
            <Lock className={`${isDarkMode ? 'text-indigo-400' : 'text-blue-600'}`} size={20} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('settings.securityAndPassword', 'Şifre ve Güvenlik')}
            </h2>
          </div>
          <button
            className={`w-full flex items-center justify-between p-4 rounded-lg border touch-target transition-colors
              ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/30' : 'border-gray-300 hover:bg-gray-50'}`}
            onClick={() => navigateToScreen('securitySettings')}
            style={{ minHeight: '60px' }}
          >
            <div className="flex items-center space-x-3">
              <Shield className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} size={18} />
              <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{t('settings.manageSecurity', 'Şifre ve güvenlik ayarlarını yönet')}</span>
            </div>
            <ChevronRight size={16} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
          </button>
        </div>
      </div>
      
      {/* Settings feedback toast */}
      {showFeedback && (
        <div className="fixed bottom-20 left-0 right-0 flex justify-center z-50">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2
            ${isDarkMode ? 'bg-indigo-900/90 text-indigo-100' : 'bg-blue-600 text-white'}`}
          >
            <Check size={16} />
            <span>{feedbackMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default withTranslation(SettingsScreen);
